import Controller from '@ember/controller'
import type Store from '@ember-data/store'
import { service } from '@ember/service'
import { isEmpty } from '@ember/utils'
import { task } from 'ember-concurrency'
import type { Log } from '@blakeelearning/log'
import type Student from 'teacher-ui-client/models/student'
import type BookLibraryShare from 'teacher-ui-client/models/book-library/share'

export default class BookLibrarySharesByCollectionShowController extends Controller {
  @service log!: Log

  @service store!: Store

  /** We place this task in the controller, so that it can be long-lived when query params change, but is also only
   * typically performed after shares are loaded in by the parent routes.
   *
   * However, it can occur where this task is called before the share is not yet loaded (eg, refresh w/ share row open)
   * For this reason, most of the time it's a peekRecord, but in some cases we do a findRecord.
   */
  fetchStudentsForShare = task({ restartable: true }, async ({ shareId }: { shareId: string }) => {
    const share =
      this.store.peekRecord<BookLibraryShare>('book-library/share', shareId) ??
      (await this.store.findRecord<BookLibraryShare>('book-library/share', shareId))

    if (!share) {
      this.log.error(`could not find book library share record with id ${shareId} in store`)
      return []
    }
    // @ts-expect-error - something wrong with relationship reference types
    const studentIds = share.hasMany<Student, 'students'>('students').ids() || []
    if (isEmpty(studentIds)) return []

    // Could be a lot of student ids, best to send this one as post.
    return (
      await this.store.query<Student>(
        'student',
        {
          filter: { id: studentIds },
          // @ts-expect-error - include types not correct
          include: 'school-classes',
        },
        { adapterOptions: { usePost: true } },
      )
    ).slice()
  })
}
